import React, { useState } from "react";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useI18n } from "../lib/i18n";
import { publicApi } from "../lib/api";
import MessageBubble from "./MessageBubble";
import QuotaBanner from "./QuotaBanner";

export default function ChatFloatingButton() {
  const { t } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "ai", text: t("chat.welcome"), sources: [] },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [quota, setQuota] = useState(null);

  // Ne pas afficher le bouton si on est déjà sur la page Chat ou dans l'admin
  if (location.pathname === "/chat" || location.pathname.startsWith("/admin")) {
    return null;
  }

  const refreshQuota = async () => {
    try {
      const adminToken = localStorage.getItem("lexhab_token");
      if (adminToken) {
        setQuota({ is_admin: true, is_subscribed: true, quota_remaining: 9999, daily_limit: 0, questions_used_today: 0 });
        return;
      }
      const { data } = await publicApi.quota();
      setQuota(data);
    } catch (e) {}
  };

  const send = async (question) => {
    const q = (question ?? input).trim();
    if (!q || loading) return;
    
    setMessages((m) => [...m, { role: "user", text: q }]);
    setInput("");
    setLoading(true);
    
    try {
      const { data } = await publicApi.chat({
        question: q,
        language: "fr",
        user_type: "public",
      });
      setMessages((m) => [
        ...m,
        {
          role: "ai",
          text: data.answer,
          sources: data.sources || [],
          queryLogId: data.query_log_id,
        },
      ]);
    } catch (e) {
      if (e?.response?.status === 429 && e?.response?.data?.detail?.quota_exceeded) {
        refreshQuota();
        setMessages((m) => m.slice(0, -1));
        setInput(q);
        return;
      }
      const msg = "Une erreur est survenue. Merci de réessayer.";
      setMessages((m) => [...m, { role: "ai", text: msg, sources: [] }]);
    } finally {
      setLoading(false);
      refreshQuota();
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    send();
  };

  const handleClose = () => {
    setIsOpen(false);
    // Reset messages when closing
    setMessages([{ role: "ai", text: t("chat.welcome"), sources: [] }]);
    setInput("");
  };

  if (isOpen) {
    return (
      <div className="fixed bottom-6 right-6 z-50 w-full max-w-md">
        <div className="bg-white rounded-lg shadow-2xl border border-primary/20 overflow-hidden flex flex-col" style={{ height: "600px" }}>
          {/* Header */}
          <div className="bg-primary text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageCircle size={20} />
              <span className="font-medium text-sm">{t("chat.floatingButton")}</span>
            </div>
            <button
              onClick={handleClose}
              className="text-white/80 hover:text-white transition-colors"
              aria-label="Fermer le chat"
            >
              <X size={20} />
            </button>
          </div>

          {/* Quota Banner */}
          {quota && !quota.is_subscribed && !quota.is_admin && (
            <div className="px-4 py-2 bg-muted/50 border-b border-primary/10">
              <QuotaBanner quota={quota} onSubscribe={() => navigate("/pricing")} compact />
            </div>
          )}

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-muted/30">
            {messages.map((m, i) => (
              <MessageBubble
                key={i}
                role={m.role}
                text={m.text}
                sources={m.sources}
                compact
              />
            ))}
            {loading && (
              <div className="flex gap-2 items-center text-foreground/60 text-sm">
                <Loader2 size={16} className="animate-spin text-primary" />
                <span className="text-xs">{t("chat.loading")}</span>
              </div>
            )}
          </div>

          {/* Input Form */}
          <form onSubmit={onSubmit} className="p-3 bg-white border-t border-primary/10 flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t("chat.placeholder")}
              disabled={loading}
              maxLength={500}
              className="flex-1 bg-transparent px-3 py-2 text-sm outline-none placeholder:text-foreground/40 border border-primary/20 rounded-sm focus:border-primary transition-colors"
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              className="bg-primary text-white px-4 py-2 rounded-sm text-sm font-medium hover:bg-primary-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              <Send size={16} />
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <button
      onClick={() => {
        setIsOpen(true);
        refreshQuota();
      }}
      className="fixed bottom-6 right-6 z-50 group"
      data-testid="chat-floating-button"
    >
      {/* Conteneur principal avec effet de shadow et animation */}
      <div className="relative flex items-center gap-3 bg-primary text-white px-5 py-3.5 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 hover:bg-primary-light">
        {/* Icône de chat animée */}
        <div className="relative">
          <MessageCircle size={22} strokeWidth={2} />
          {/* Petit point pulse pour indiquer la disponibilité */}
          <span className="absolute -top-1 -right-1 flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-accent"></span>
          </span>
        </div>
        
        {/* Texte visible au hover */}
        <span className="max-w-0 overflow-hidden group-hover:max-w-xs group-hover:mr-2 transition-all duration-300 ease-in-out whitespace-nowrap font-medium text-sm">
          {t("chat.floatingButton")}
        </span>
        
        {/* Flèche directionnelle */}
        <svg 
          width="16" 
          height="16" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2" 
          className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 -mt-0.5"
        >
          <path d="M7 17L17 7M17 7H7M17 7V17" />
        </svg>
      </div>
      
      {/* Tooltip pour mobile */}
      <div className="absolute bottom-full right-0 mb-3 px-3 py-2 bg-primary text-white text-xs rounded-sm shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none whitespace-nowrap hidden sm:block">
        {t("chat.floatingTooltip")}
        {/* Petite flèche du tooltip */}
        <div className="absolute top-full right-4 border-4 border-transparent border-t-primary"></div>
      </div>
    </button>
  );
}
